import React from 'react';
import { 
  DocumentTextIcon, 
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

/**
 * Invoice Component
 * Generates a professional invoice from cart items
 * Can be exported as PDF or sent via WhatsApp
 */
const Invoice = ({ invoiceData, onClose }) => {
  if (!invoiceData) return null;

  const {
    invoiceNumber,
    invoiceDate,
    customerInfo,
    items,
    subtotal,
    shipping,
    discount,
    tax,
    total,
    savings
  } = invoiceData;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full my-8">
        <div className="p-6 sm:p-8" id="invoice-content">
          {/* Invoice Header */}
          <div className="flex justify-between items-start mb-8 pb-6 border-b-2 border-primary-600">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-2 rounded-lg">
                  <span className="text-white font-bold text-xl">🦷</span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">TeethKart</h1>
                  <p className="text-sm text-gray-600">India's Leading Dental Store</p>
                </div>
              </div>
              <div className="text-sm text-gray-600 space-y-1 mt-4">
                <div className="flex items-center">
                  <MapPinIcon className="h-4 w-4 mr-2" />
                  <span>Group dental Shopee, A102. Sai Sanman CHS Ltd</span>
                </div>
                <div className="ml-6">Vasai Road West 401202</div>
                <div className="flex items-center">
                  <PhoneIcon className="h-4 w-4 mr-2" />
                  <span>+91-8999-250-629</span>
                </div>
                <div className="flex items-center">
                  <EnvelopeIcon className="h-4 w-4 mr-2" />
                  <span>Omprahalladbhandary@gmail.com</span>
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <div className="bg-primary-600 text-white px-4 py-2 rounded-lg inline-block mb-3">
                <DocumentTextIcon className="h-6 w-6 inline-block mr-2" />
                <span className="font-bold text-lg">INVOICE</span>
              </div>
              <div className="text-sm space-y-1">
                <div><span className="font-semibold">Invoice #:</span> {invoiceNumber}</div>
                <div><span className="font-semibold">Date:</span> {invoiceDate}</div>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          {customerInfo && (
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Bill To:</h3>
              <div className="text-sm text-gray-700 space-y-1">
                <div className="font-medium text-gray-900">{customerInfo.name || 'Customer'}</div>
                {customerInfo.email && <div>{customerInfo.email}</div>}
                {customerInfo.phone && <div>Phone: {customerInfo.phone}</div>}
                {customerInfo.address && <div>{customerInfo.address}</div>}
              </div>
            </div>
          )}

          {/* Invoice Items Table */}
          <div className="mb-6">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-100 border-b-2 border-gray-300">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">#</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Product</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Qty</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Unit Price</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {items.map((item, index) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-700">{index + 1}</td>
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900">{item.name}</div>
                      {item.category && (
                        <div className="text-xs text-gray-500">{item.category}</div>
                      )}
                      {item.brand && (
                        <div className="text-xs text-gray-500">Brand: {item.brand}</div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center text-gray-700">{item.quantity}</td>
                    <td className="py-3 px-4 text-right text-gray-700">
                      ₹{item.price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-gray-900">
                      ₹{item.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Invoice Totals */}
          <div className="flex justify-end mb-6">
            <div className="w-full sm:w-80 space-y-2">
              <div className="flex justify-between py-2 text-gray-700">
                <span>Subtotal:</span>
                <span className="font-medium">
                  ₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>
              
              {discount > 0 && (
                <div className="flex justify-between py-2 text-green-600">
                  <span>Discount:</span>
                  <span className="font-medium">
                    -₹{discount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              
              {savings > 0 && (
                <div className="flex justify-between py-2 text-green-600">
                  <span>You Saved:</span>
                  <span className="font-medium">
                    ₹{savings.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              
              <div className="flex justify-between py-2 text-gray-700">
                <span>Shipping:</span>
                <span className="font-medium">
                  {shipping > 0 
                    ? `₹${shipping.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` 
                    : 'FREE'}
                </span>
              </div>
              
              {tax > 0 && (
                <div className="flex justify-between py-2 text-gray-700">
                  <span>Tax (GST 18%):</span>
                  <span className="font-medium">
                    ₹{tax.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              
              <div className="flex justify-between py-3 border-t-2 border-gray-300 text-lg font-bold text-gray-900">
                <span>Total:</span>
                <span className="text-primary-600">
                  ₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          {/* Footer Notes */}
          <div className="border-t border-gray-200 pt-6">
            <div className="text-sm text-gray-600 space-y-2">
              <p className="font-semibold text-gray-900">Payment Terms:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Payment due within 7 days of invoice date</li>
                <li>All products are covered by manufacturer warranty</li>
                <li>Returns accepted within 7 days of delivery</li>
                <li>Free shipping on orders above ₹5,000</li>
              </ul>
            </div>
            
            <div className="mt-6 flex justify-center">
              <img 
                src="/barcode.jpeg" 
                alt="Payment Barcode" 
                className="max-w-xs w-full h-auto"
              />
            </div>
            
            <div className="mt-6 text-center text-sm text-gray-600 bg-gray-50 py-3 rounded">
              <p className="font-semibold text-gray-900 mb-1">Thank you for your business!</p>
              <p>For any queries, contact us at +91-8999-250-629 or Omprahalladbhandary@gmail.com</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex flex-wrap gap-3 justify-end border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Print Invoice
          </button>
        </div>
      </div>
    </div>
  );
};

export default Invoice;


